import { useAppSelector } from '@lib/store/hooks';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { type ReactNode, useMemo } from 'react';

interface AppThemeProps {
  children: ReactNode;
}

export const AppTheme = ({ children }: AppThemeProps): JSX.Element => {
  const mode = useAppSelector((state) => state.colorMode.mode);
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: mode === 'dark' ? '#ff264d' : '#e61d2a' },
          success: { main: mode === 'dark' ? '#25b34b' : '#146c2e' },
          background: {
            default: mode === 'dark' ? '#0e1012' : '#f4f6f8',
            paper: mode === 'dark' ? '#1c1f23' : '#ffffff',
          },
          text: {
            primary: mode === 'dark' ? '#f4f6f8' : '#16181b',
            secondary: mode === 'dark' ? '#9aa0a6' : '#5a626c',
          },
          divider: mode === 'dark' ? '#34393f' : '#dee2e6',
        },
        typography: {
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          button: { textTransform: 'none', fontWeight: 600 },
          overline: { lineHeight: 1.5 },
        },
        shape: { borderRadius: 12 },
        components: {
          MuiOutlinedInput: {
            styleOverrides: {
              root: ({ theme }) => ({
                '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: theme.palette.text.primary,
                    borderWidth: 2,
                  },
              }),
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: ({ theme }) => ({
                '&.Mui-focused:not(.Mui-error)': {
                  color: theme.palette.text.primary,
                },
              }),
            },
          },
        },
      }),
    [mode]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
